import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import {
    GetActiveManagementRequestParams,
    useActiveManagementApi,
    useApi,
} from '@/hooks'
import { Button, Icon } from '@/components/atoms'
import { MenuItem } from '@/components/common/MenuItem/MenuItem'
import { Dropdown } from '@/components/common/Dropdown/Dropdown'
import { DailyEmailExportLimitModalContent } from '@/components/organisms/EmailExportModal/DailyEmailExportLimitModalContent'
import { EmailExportModal } from '@/components/organisms/EmailExportModal/EmailExportModal'
import useToastStore from '@/stores/useToastStore/useToastStore'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import {
    ExportErrorResponse,
    ExportLimitErrorDetails,
    IntervalReportSettings,
    StartExportRequest,
} from '@/hooks/useApi/useActiveManagementApi/types'
import { useEmailExportModalParams } from '@/components/organisms/EmailExportModal/useEmailExportModalParams'
import { Spinner } from '@/components'
import { mapIntervalDataQueryFromGetParams } from '../../utils/mapIntervalDataQueryFromGetParams'

export type IntervalDataExportDropdownProps = {
    filters: Partial<GetActiveManagementRequestParams>
    reportSettings: IntervalReportSettings
}

type IntervalDataExportMeta = {
    errorIdentifier?: string
    count?: number
    limit?: number
}

export const IntervalDataExportDropdown: React.FC<
    IntervalDataExportDropdownProps
> = ({ filters, reportSettings }) => {
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = useState(false)
    const [isExporting, setIsExporting] = useState(false)
    const {
        exportByEmailOpen,
        onExportByEmailClose,
        onExportByEmailSuccess,
        reportGenerationOpen,
        onReportGenerationClose,
        onExportByEmailOpen,
    } = useEmailExportModalParams()
    const { useIntervalDataStreamedQuery } = useActiveManagementApi()
    const { useStartExportMutation } = useApi()
    const customerId = useAuthStore((state) => state.customer?.customerId)
    const startExportMutation = useStartExportMutation()

    const {
        data: streamedIntervalData,
        downloadXlsx,
        isError,
        isFetching,
    } = useIntervalDataStreamedQuery(filters, { reportSettings })

    const data = streamedIntervalData?.data ?? []
    const hasData = Array.isArray(data) && data.length > 0

    const isExportButtonDisabled =
        !hasData || isError || isFetching || isExporting
    const addAlert = useToastStore((state) => state.addAlert)
    const setModalState = useGlobalModalStore((state) => state.setModalState)

    const isDailyLimitExceeded = (
        currentCount?: number,
        limit?: number
    ): boolean => {
        return (
            typeof currentCount === 'number' &&
            typeof limit === 'number' &&
            currentCount >= limit
        )
    }

    const openDailyLimitReachedModal = useCallback(() => {
        const closeModal = () => {
            setModalState({ isOpen: false })
        }

        setModalState({
            isOpen: true,
            title: t('common.exportByEmail.daily_limit_reached.title'),
            description: t(
                'common.exportByEmail.daily_limit_reached.description'
            ),
            icon: 'warning',
            iconClassName: 'text-orange-500',
            content: () => (
                <DailyEmailExportLimitModalContent onClose={closeModal} />
            ),
            onClose: closeModal,
        })
    }, [t, setModalState])

    const showLimitReachedFlow = useCallback(() => {
        onExportByEmailClose()
        setTimeout(openDailyLimitReachedModal, 0)
    }, [onExportByEmailClose, openDailyLimitReachedModal])

    const handleExportByEmailSubmitError = useCallback(
        (error: unknown) => {
            const isLimitExceeded =
                axios.isAxiosError(error) && error.response?.status === 429
            const responseData = axios.isAxiosError(error)
                ? (error.response?.data as
                      | { details?: ExportLimitErrorDetails }
                      | undefined)
                : undefined
            const details = responseData?.details
            const currentCount = details?.currentCount
            const limit = details?.limit
            const showLimitModal =
                isLimitExceeded || isDailyLimitExceeded(currentCount, limit)

            if (showLimitModal) {
                showLimitReachedFlow()
            } else {
                onExportByEmailClose()
                addAlert({
                    variant: 'error',
                    title: t('reporting.export_report.error_toast.title'),
                    description: t(
                        'reporting.export_report.error_toast.description'
                    ),
                })
            }
        },
        [showLimitReachedFlow, onExportByEmailClose, addAlert, t]
    )

    const handleExportXlsx = async () => {
        setIsOpen(false)
        setIsExporting(true)

        try {
            await downloadXlsx()
        } catch (error) {
            const { errorIdentifier } = (error ?? {}) as IntervalDataExportMeta

            if (errorIdentifier === 'limitExceeded') {
                onExportByEmailOpen()
                return
            }

            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
        } finally {
            setIsExporting(false)
        }
    }

    const handleSubmitExportByEmail = useCallback(async () => {
        const customerIdNum = Number(customerId)
        if (!filters.type || !filters.startDate || !filters.endDate) {
            addAlert({
                variant: 'error',
                title: t('reporting.export_report.error_toast.title'),
                description: t(
                    'reporting.export_report.error_toast.description'
                ),
            })
            return
        }
        const query = mapIntervalDataQueryFromGetParams(filters, reportSettings)
        const payload: StartExportRequest = {
            exportType: 'INTERVAL_DATA',
            customerId: customerIdNum,
            query,
        }
        const result = (await startExportMutation.mutateAsync(
            payload
        )) as unknown
        const hasErrorType =
            typeof result === 'object' &&
            result !== null &&
            'type' in result &&
            (result as Record<string, unknown>).type === 'error'
        if (hasErrorType) {
            const { details } = result as ExportErrorResponse
            if (isDailyLimitExceeded(details?.currentCount, details?.limit)) {
                showLimitReachedFlow()
                return
            }
        }
    }, [
        customerId,
        filters,
        reportSettings,
        addAlert,
        t,
        startExportMutation,
        showLimitReachedFlow,
    ])

    return (
        <>
            <EmailExportModal
                exportByEmailOpen={exportByEmailOpen}
                onExportByEmailClose={onExportByEmailClose}
                onExportByEmailSuccess={onExportByEmailSuccess}
                reportGenerationOpen={reportGenerationOpen}
                onReportGenerationClose={onReportGenerationClose}
                onSubmitExport={handleSubmitExportByEmail}
                onSubmitExportError={handleExportByEmailSubmitError}
            />
            <Dropdown
                isOpen={isOpen}
                onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    setIsOpen(!isOpen)
                }}
                name="interval-data-export-dropdown"
                buttonProps={{
                    name: 'interval-data-export-dropdown',
                    children: (
                        <>
                            {t('common.export_report_dropdown.label')}
                            {isExporting ? (
                                <Spinner size={16} width={3} />
                            ) : (
                                <Icon size="small" name="keyboard_arrow_down" />
                            )}
                        </>
                    ),
                    variant: 'outlinePrimary',
                    size: 'large',
                    disabled: isExportButtonDisabled,
                }}
                buttonComponent={Button}
                horizontalAlign="right"
                dropdownClassname="py-3 !min-w-[auto]"
            >
                <MenuItem
                    path="/"
                    variant="ghost"
                    label={t('common.export_report_dropdown.options.xls')}
                    render={({ renderContents }) => (
                        <button
                            className="group w-full outline-none"
                            onClick={() => {
                                void handleExportXlsx()
                            }}
                            data-testid="export-interval-data-excel-button"
                        >
                            {renderContents()}
                        </button>
                    )}
                />
            </Dropdown>
        </>
    )
}
